import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { findIndustry, INDUSTRIES, CITIES, BUSINESS } from "@/lib/site-data";

export const Route = createFileRoute("/industries/$industry")({
  loader: ({ params }) => {
    const industry = findIndustry(params.industry);
    if (!industry) throw notFound();
    return { industry };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Industry not found" }] };
    const i = loaderData.industry;
    const title = `${i.name} | ${BUSINESS.shortName}`;
    const description = `${i.tagline} ${i.description.slice(0, 110)}`.slice(0, 158);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: `/industries/${params.industry}` },
      ],
      links: [{ rel: "canonical", href: `https://lehitradessaeo.com/industries/${params.industry}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: i.name,
            serviceType: "Search engine optimization",
            audience: { "@type": "Audience", audienceType: i.trade },
            description: i.description,
            provider: {
              "@type": "ProfessionalService",
              name: BUSINESS.name,
              telephone: BUSINESS.phone,
            },
            areaServed: CITIES.map((c) => ({
              "@type": "City",
              name: `${c.name}, ${c.region}`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: i.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-3xl font-black">Industry not found</h1>
      <p className="mt-2 text-muted-foreground">
        <Link to="/industries" className="underline">See all industries</Link>.
      </p>
    </main>
  ),
  component: IndustryPage,
});

function IndustryPage() {
  const { industry: i } = Route.useLoaderData();
  const others = INDUSTRIES.filter((x) => x.slug !== i.slug);
  return (
    <main>
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            {i.trade}
          </p>
          <h1 className="mt-4 font-display text-5xl tracking-tight md:text-6xl">
            {i.name}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">{i.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Get a free audit
            </Link>
            <a
              href={BUSINESS.phoneHref}
              className="rounded-full px-6 py-3 text-sm font-medium text-foreground hover:bg-secondary"
            >
              Call {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="grid gap-12 md:grid-cols-3">
          <article className="md:col-span-2 space-y-12">
            <div>
              <h2 className="font-display text-3xl tracking-tight">How we help {i.trade.toLowerCase()}</h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">{i.description}</p>
            </div>

            <div>
              <h3 className="font-display text-2xl tracking-tight">What's holding you back</h3>
              <ul className="mt-4 grid gap-2">
                {i.pains.map((p: string) => (
                  <li key={p} className="rounded-xl border border-border bg-card p-4 text-sm">
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-display text-2xl tracking-tight">What we do about it</h3>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {i.wins.map((w: string) => (
                  <li key={w} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-sm">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    {w}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="font-display text-3xl tracking-tight">Frequently asked questions</h2>
              <div className="mt-6 divide-y divide-border border-y border-border">
                {i.faqs.map((f: { q: string; a: string }) => (
                  <details key={f.q} className="group py-5">
                    <summary className="cursor-pointer list-none font-medium">
                      <span className="mr-2 text-primary">+</span>
                      {f.q}
                    </summary>
                    <p className="mt-3 pl-6 text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </article>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-border bg-card p-6">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Talk to us</p>
              <p className="mt-3 font-display text-2xl">{BUSINESS.phone}</p>
              <p className="mt-1 text-sm text-muted-foreground">{BUSINESS.hours}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Service areas
              </h3>
              <ul className="mt-3 space-y-1.5 text-sm">
                {CITIES.map((c) => (
                  <li key={c.slug}>
                    <Link to="/service-areas/$city" params={{ city: c.slug }} className="text-muted-foreground hover:text-foreground">
                      {i.trade} in {c.name}, {c.region}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Other industries
              </h3>
              <ul className="mt-3 space-y-1.5 text-sm">
                {others.map((o) => (
                  <li key={o.slug}>
                    <Link to="/industries/$industry" params={{ industry: o.slug }} className="text-muted-foreground hover:text-foreground">
                      {o.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}